<script setup>
import { computed, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    defaultValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    options: {
        type: Array,
        default: () => []
    },
    visible: {
        type: Boolean,
        default: true
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    }
});

const emit = defineEmits(['update:modelValue', 'input']);
const selectedValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isReadonly = computed(() => props.readonly);

onMounted(() => {
    if (props.defaultValue) {
        selectedValue.value = props.defaultValue;
        emit('update:modelValue', selectedValue.value);
    }
});

const onChange = (event) => {
    ValidationMsg.value = '';
    const value = event.target.value;
    selectedValue.value = value;
    emit('update:modelValue', value);

    const selectedOption = props.options.find((option) => option.value === value);
    emit('input', {
        id: props.inputId,
        value: value,
        label: selectedOption ? selectedOption.text : value
    });
};

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

watch(
    () => props.modelValue,
    (newVal) => {
        selectedValue.value = newVal;
    },
    { immediate: true }
);
</script>

<template>
    <div
        v-if="visible"
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label :for="inputId" :class="['label-font mb-1', required ? 'label-required' : 'text-black dark:text-gray-300']">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div :class="['radio-group flex flex-wrap w-full items-center min-h-[40px]', { 'error-border': ValidationMsg }]">
            <div v-for="option in options" :key="option.value" class="radio-option">
                <input type="radio" :id="`${inputId}_${option.value}`" :name="inputId" :value="option.value" v-model="selectedValue" :disabled="isReadonly" @change="onChange" :class="['radio-input']" />
                <label :for="`${inputId}_${option.value}`" class="radio-option-label" :title="option.value">{{ option.text }}</label>
            </div>
        </div>
    </div>
</template>

<style scoped>
.label-font {
    font-family: 'SCoreDream600';
    display: flex;
    /* justify-content: space-between; */
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171;
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.error-border {
    border-color: #f87171 !important;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.radio-group {
    padding: 0.75rem;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    transition: border-color 0.3s ease;
}

.radio-option {
    display: inline-flex;
    align-items: center;
    margin-right: 1rem;
    gap: 0.1rem;
    /* margin-bottom: 0.5rem; */
}

.radio-input {
    appearance: none;
    -webkit-appearance: none;
    width: 1rem;
    height: 1rem;
    border: 0.2rem solid #d1d5db;
    border-radius: 50%;
    outline: none;
    cursor: pointer;
    margin-right: 0.5rem;
}

.radio-option-label {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    color: #374151;
    cursor: pointer;
}

.radio-input:disabled,
.radio-input:disabled + .radio-option-label {
    cursor: default;
}

.radio-input:checked {
    border-color: #0038ea;
    background-color: #0038ea;
    box-shadow: inset 0 0 0 0.12rem #fff;
}

.radio-input:focus {
    border-color: #0038ea;
    background-color: #0038ea;
    box-shadow: inset 0 0 0 0.15rem #fff;
}
</style>
